// SPDX-FileCopyrightText: Copyright (c) 2026 NVIDIA CORPORATION & AFFILIATES. All rights reserved.
// SPDX-License-Identifier: Apache-2.0

import { html, LitElement } from 'lit';
import { property } from 'lit/decorators/property.js';
import { state } from 'lit/decorators/state.js';
import { queryAssignedElements } from 'lit/decorators/query-assigned-elements.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { useStyles } from '@nvidia-elements/core/internal';
import styles from './markdown.css?inline';

// Type definitions for the lazy-loaded MarkdownIt module
interface MarkdownItInstance {
  render(markdown: string): string;
}

interface MarkdownItConstructor {
  new (config?: Record<string, unknown>): MarkdownItInstance;
}

/** Default configuration for markdown-it parser */
const CONFIG = {
  html: true,
  linkify: true,
  typographer: true,
  breaks: false
} as const;

/**
 * A Web Component that renders markdown content.
 *
 * Supports two input modes:
 * - `source` property: Markdown content set programmatically
 * - `<template>` slot: Markdown content inside a template element
 *
 * @element nve-markdown
 * @cssprop --padding - The padding of the component
 * @description A Web Component that inputs markdown content and renders it as HTML formatted with Elements styles.
 * @since 0.0.0
 * @entrypoint \@nvidia-elements/markdown/markdown
 * @slot - The slot can contain a template element with markdown content
 * @alpha
 */
export class Markdown extends LitElement {
  static styles = useStyles([styles]);

  static readonly metadata = {
    tag: 'nve-markdown',
    version: '0.0.0'
  };

  static elementDefinitions = {};

  /**
   * Markdown content source, settable programmatically
   */
  @property({ type: String }) source = '';

  /**
   * Markdown-it constructor (lazy-loaded)
   */
  #MarkdownItConstructor?: MarkdownItConstructor;

  /**
   * Markdown-it instance for parsing markdown content (lazy-loaded)
   */
  #markdownIt?: MarkdownItInstance;

  /**
   * Internal state for the content to render
   */
  @state() private renderedHtml = '';

  /**
   * Tracks the currently pending markdown parse so tests and consumers can await
   * `updateComplete` before reading rendered output.
   */
  #pendingMarkdownParsing: Promise<void> = Promise.resolve();

  /**
   * Query assigned elements in the default slot
   */
  @queryAssignedElements({ slot: '' }) private slotElements!: HTMLElement[];

  render() {
    return html`
      <div internal-host>${unsafeHTML(this.renderedHtml)}</div>
      <slot @slotchange=${this.#processSlottedContent}></slot>
    `;
  }

  protected override async getUpdateComplete(): Promise<boolean> {
    const result = await super.getUpdateComplete();
    const pendingMarkdownParsing = this.#pendingMarkdownParsing;

    await pendingMarkdownParsing;
    await super.getUpdateComplete();

    return result;
  }

  protected updated(changedProperties: Map<string | number | symbol, unknown>) {
    if (changedProperties.has('source')) {
      if (this.source) {
        this.#renderMarkdown(this.source);
      } else {
        // Source cleared, check for slotted content or clear rendered output
        this.#clearRenderedHtml();
        this.#processSlottedContent();
      }
    }
  }

  /**
   * Process slotted content to extract markdown from template elements.
   * Only processes when source property is empty (source takes priority).
   */
  #processSlottedContent(): void {
    // Don't process slotted content if we have content from the source property
    if (this.source) return;

    const templateElement = this.slotElements.find(e => e.localName === 'template') as HTMLTemplateElement | undefined;

    if (!templateElement) {
      this.#clearRenderedHtml();
      return;
    }

    const content = templateElement.innerHTML.trim();

    this.#renderMarkdown(content);
  }

  #renderMarkdown(markdown: string): void {
    this.#pendingMarkdownParsing = this.#parseMarkdown(markdown)
      .then(parsed => {
        this.renderedHtml = parsed;
      })
      .catch(_error => {
        // Don't update renderedHtml on parsing error - keep previous content
        console.debug('Markdown parsing failed, keeping previous content');
      });
  }

  #clearRenderedHtml(): void {
    this.#pendingMarkdownParsing = Promise.resolve();
    this.renderedHtml = '';
  }

  /**
   * Parse markdown content to HTML using markdown-it.
   * Lazy-loads the markdown-it instance only when needed for better performance.
   *
   * @param markdown - The markdown content to parse
   * @returns Parsed HTML content or empty string if input is empty
   */
  async #parseMarkdown(markdown: string): Promise<string> {
    if (!markdown) return '';

    // Lazy-load markdown-it constructor only when needed
    if (!this.#MarkdownItConstructor) {
      const module = await import('markdown-it');
      this.#MarkdownItConstructor = module.default;
    }

    // Lazy-load markdown-it instance only when needed
    if (!this.#markdownIt) {
      this.#markdownIt = new this.#MarkdownItConstructor!(CONFIG);
    }

    try {
      return this.#markdownIt.render(markdown);
    } catch (error) {
      console.error('Error parsing markdown:', error);
      // Re-throw the error so calling methods can handle it
      throw error;
    }
  }
}
